import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import { Camera, Languages, Volume2, ArrowRight, Sparkles, Globe, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function LandingPage() {
  const { loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      // Scroll to the sign-in section or show the button
      document.getElementById("google-signin")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      await loginWithGoogle(credentialResponse);
      navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-hidden">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              LingoLens
            </span>
          </div>
          {user ? (
            <Button
              data-testid="nav-get-started-btn"
              onClick={() => navigate("/dashboard")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2 font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
            >
              Dashboard
            </Button>
          ) : (
            <div data-testid="nav-google-signin">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => console.error("Google Login Failed")}
                shape="pill"
                text="signin"
                size="medium"
              />
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-8">
                <Sparkles className="w-4 h-4" />
                AI-Powered Language Learning
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-tight"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                Snap a Photo,{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                  Learn a Language
                </span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-500 max-w-lg leading-relaxed">
                Point your camera at any text — signs, menus, books — and instantly translate it to any language.
                Hear the pronunciation and learn as you go.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 items-center" id="google-signin">
                {user ? (
                  <Button
                    data-testid="hero-get-started-btn"
                    onClick={() => navigate("/dashboard")}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 py-4 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  >
                    Start Translating
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => console.error("Google Login Failed")}
                    shape="pill"
                    text="continue_with"
                    size="large"
                    width="280"
                  />
                )}
              </div>
            </div>

            <div className="relative animate-slide-up delay-200 hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-indigo-200/50">
                <img
                  src="https://images.unsplash.com/photo-1758612898788-38a249839c1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                  alt="Language learning with camera"
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/30 to-transparent" />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl shadow-indigo-100/30 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-400 flex items-center justify-center">
                    <Languages className="w-5 h-5 text-yellow-900" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">Translated to</p>
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>130+ Languages</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-100/40 rounded-full blur-3xl -z-10" />
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-base md:text-lg font-bold text-indigo-600 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              How It Works
            </h2>
            <p className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Three Simple Steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Camera,
                color: "bg-indigo-100 text-indigo-600",
                title: "Capture or Upload",
                desc: "Use your camera to snap a photo or upload an image with text you want to translate."
              },
              {
                icon: Languages,
                color: "bg-yellow-100 text-yellow-700",
                title: "Instant Translation",
                desc: "AI extracts the text and translates it into your chosen language in seconds."
              },
              {
                icon: Volume2,
                color: "bg-pink-100 text-pink-600",
                title: "Listen & Learn",
                desc: "Hear the translated text spoken aloud to perfect your pronunciation."
              }
            ].map((step, i) => (
              <div
                key={i}
                className="group bg-white rounded-3xl border border-slate-100 shadow-xl shadow-indigo-100/20 p-8 hover:border-indigo-200 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-2xl ${step.color} flex items-center justify-center mb-6`}>
                  <step.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-8 md:p-12 text-white">
              <Globe className="w-10 h-10 mb-6 text-indigo-200" />
              <h3 className="text-2xl font-extrabold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                130+ Languages Supported
              </h3>
              <p className="text-indigo-100 leading-relaxed">
                From Spanish and French to Japanese and Arabic — translate text into virtually any language.
                Perfect for travel, study, or everyday curiosity.
              </p>
            </div>
            <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-indigo-100/20 p-8 md:p-12">
              <BookOpen className="w-10 h-10 mb-6 text-yellow-500" />
              <h3 className="text-2xl font-extrabold text-slate-900 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Built for Learning
              </h3>
              <p className="text-slate-500 leading-relaxed">
                Designed for kids and adults alike. See the original text alongside the translation,
                hear pronunciation, and build your vocabulary naturally.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-400/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Ready to Explore Languages?
              </h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto">
                Start translating images in seconds. Free to get started.
              </p>
              <div className="flex justify-center" data-testid="cta-get-started-btn">
                {user ? (
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full px-8 py-4 font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  >
                    Get Started Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => console.error("Google Login Failed")}
                    shape="pill"
                    text="continue_with"
                    size="large"
                    width="280"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Camera className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-bold text-slate-600" style={{ fontFamily: 'Nunito, sans-serif' }}>LingoLens</span>
          </div>
          <p className="text-xs text-slate-400">Powered by AI</p>
        </div>
      </footer>
    </div>
  );
}
